import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CalendarClock, Plus, Sun, Moon } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "", description: "", tipo: "5x2", dias_trabalhados: 5, dias_folga: 2,
  hora_entrada: "08:00", hora_saida: "18:00", intervalo_min: 60,
  carga_semanal: 44, carga_mensal: 220, turno: "diurno",
};

export default function SchedulesPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("rh_admin");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/work-schedules");
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/work-schedules", {
        ...form,
        dias_trabalhados: Number(form.dias_trabalhados),
        dias_folga: Number(form.dias_folga),
        intervalo_min: Number(form.intervalo_min),
        carga_semanal: Number(form.carga_semanal),
        carga_mensal: Number(form.carga_mensal),
      });
      toast.success("Escala criada");
      setOpen(false); setForm(empty); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="schedules-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Jornadas</div>
          <h1 className="font-display text-3xl font-semibold">Escalas &amp; Horários de trabalho</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            Cadastre escalas (5x2, 6x1, 12x36, revezamento) e vincule a colaboradores.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setOpen(true)} className="bg-[#234935] hover:bg-[#2c5c43]" data-testid="schedule-new-btn">
            <Plus className="h-4 w-4 mr-2" /> Nova escala
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? [1,2,3].map(i => <Skeleton key={i} className="h-[180px]" />) : list.map((s) => (
          <div key={s.id} className="rounded-lg border border-border bg-white p-5" data-testid={`schedule-${s.id}`}>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-md bg-[#ECF1EE] text-[#234935]">
                <CalendarClock className="h-5 w-5" />
              </div>
              <div>
                <div className="font-display text-[15.5px] font-semibold">{s.name}</div>
                <div className="text-[11.5px] text-muted-foreground">{s.description}</div>
              </div>
              <Badge variant="outline" className={`ml-auto ${s.turno === "noturno" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                {s.turno === "noturno" ? <Moon className="h-3 w-3 mr-1" /> : <Sun className="h-3 w-3 mr-1" />}
                {s.turno}
              </Badge>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center text-[12.5px]">
              <div className="rounded-md bg-secondary/50 p-2"><div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Escala</div><div className="font-semibold">{s.tipo}</div></div>
              <div className="rounded-md bg-secondary/50 p-2"><div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Entrada</div><div className="font-semibold">{s.hora_entrada}</div></div>
              <div className="rounded-md bg-secondary/50 p-2"><div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Saída</div><div className="font-semibold">{s.hora_saida}</div></div>
            </div>
            <div className="mt-3 text-[12px] text-muted-foreground">
              Carga: {s.carga_semanal}h/semana · {s.carga_mensal}h/mês
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="font-display">Nova escala</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Nome" v={form.name} s={(v) => setForm({ ...form, name: v })} full />
            <F label="Descrição" v={form.description} s={(v) => setForm({ ...form, description: v })} full />
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["5x2","6x1","12x36","4x2","administrativo","revezamento"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider">Turno</Label>
              <Select value={form.turno} onValueChange={(v) => setForm({ ...form, turno: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="diurno">Diurno</SelectItem>
                  <SelectItem value="noturno">Noturno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <F label="Hora entrada" v={form.hora_entrada} s={(v) => setForm({ ...form, hora_entrada: v })} />
            <F label="Hora saída" v={form.hora_saida} s={(v) => setForm({ ...form, hora_saida: v })} />
            <F label="Dias trabalhados" v={form.dias_trabalhados} s={(v) => setForm({ ...form, dias_trabalhados: v })} type="number" />
            <F label="Dias folga" v={form.dias_folga} s={(v) => setForm({ ...form, dias_folga: v })} type="number" />
            <F label="Intervalo (min)" v={form.intervalo_min} s={(v) => setForm({ ...form, intervalo_min: v })} type="number" />
            <F label="Carga semanal (h)" v={form.carga_semanal} s={(v) => setForm({ ...form, carga_semanal: v })} type="number" />
            <F label="Carga mensal (h)" v={form.carga_mensal} s={(v) => setForm({ ...form, carga_mensal: v })} type="number" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-[#234935] hover:bg-[#2c5c43]" onClick={save} data-testid="schedule-save">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const F = ({ label, v, s, full, type = "text" }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <Label className="text-[11px] uppercase tracking-wider">{label}</Label>
    <Input type={type} value={v ?? ""} onChange={(e) => s(e.target.value)} />
  </div>
);
